import React, {useEffect, useMemo} from "react";
import {useTheme} from "connectui/theme/ThemeProvider";
import DropDownSelect from "../../../../connectui/component/input/dropdown/DropDownSelect";
import OutlinedDropDownBase from "../../../../connectui/component/input/dropdown/base/OutlinedDropDownBase";
import FlexContainer from "../../../../connectui/component/layout/FlexContainer";
import Text from "../../../../connectui/component/display/Text";

const INTERVAL_MAP = {
  "By Day": "day",
  "By Week": "week",
  "By Month": "month",
};

const getTimeframeDays = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  const start = new Date(startDate);
  const end = new Date(endDate);
  return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
};

const getValidIntervals = (days) => {
  const intervals = ["By Day"];

  if (days >= 14) {
    // At least 2 weeks for weekly view
    intervals.push("By Week");
  }

  if (days >= 60) {
    // At least 2 months for monthly view
    intervals.push("By Month");
  }

  return intervals;
};

const getBestInterval = (days) => {
  if (days >= 60) return "By Month";
  if (days >= 14) return "By Week";
  return "By Day";
};

const TimeframeSelector = ({
  timeframe,
  selectedInterval,
  onIntervalChange,
  sx = {},
}) => {
  const {theme} = useTheme();

  const timeframeDays = useMemo(() => {
    return getTimeframeDays(timeframe?.startDate, timeframe?.endDate);
  }, [timeframe]);

  const validIntervals = useMemo(() => {
    return getValidIntervals(timeframeDays);
  }, [timeframeDays]);

  useEffect(() => {
    if (timeframe?.startDate && timeframe?.endDate) {
      const days = getTimeframeDays(timeframe.startDate, timeframe.endDate);
      const currentIntervalValid =
        getValidIntervals(days).includes(selectedInterval);

      if (!currentIntervalValid) {
        const bestInterval = getBestInterval(days);
        onIntervalChange(bestInterval);
      }
    }
  }, [timeframe?.startDate, timeframe?.endDate, onIntervalChange]);

  return (
    <DropDownSelect
      key={`${selectedInterval}-${validIntervals.join("-")}`}
      dropDownBase={OutlinedDropDownBase}
      label={selectedInterval}
      selected={selectedInterval}
      sx={{
        label: {
          padding: theme.spacing(1.5, 3.5),
          minWidth: "100px",
        },
        ...sx,
      }}
      onChange={onIntervalChange}
    >
      {validIntervals.map((interval) => (
        <FlexContainer key={interval} value={interval} label={interval}>
          <Text color={"textPrimary"}>{interval}</Text>
        </FlexContainer>
      ))}
    </DropDownSelect>
  );
};

export {INTERVAL_MAP, getTimeframeDays, getValidIntervals, getBestInterval};

export default TimeframeSelector;
